import type { QueryClient } from '@tanstack/react-query';
import { api } from './api-client';
import { toast } from 'sonner';
import type { Auth, EmailPass } from '@/types';
import { logspot } from '@logspot/web';
import { authClient } from '@/lib/auth-client';

export const AuthService: Auth = {
  oauth: async ({ projectId }: { projectId: string }) => {
    const { data: oAuthToken } = await api.post<{ data: string }>(`/oauth`, { projectId });
    return oAuthToken;
  },

  login: async ({
    email,
    password,
  }: EmailPass): Promise<{ token: string; accountId: string }> => {
    const result = await authClient.signIn.email({
      email,
      password,
    });

    if (result.error) {
      toast.error('Sign In Failed', { description: result.error.message || 'Invalid email or password.' });
      throw new Error(result.error.message);
    }

    // Return user.id as accountId for backward compatibility
    return {
      token: '', // Sessions use cookies now, no token needed
      accountId: result.data?.user?.id || '',
    };
  },

  logout: async (queryClient?: QueryClient, redirectTo = '/sign-in') => {
    logspot.reset();
    try {
      await authClient.signOut();
      // cancelQueries before clear: aborts in-flight requests so they don't land against the now-dead session (401s); clear drops cached user data so the next sign-in starts fresh.
      await queryClient?.cancelQueries();
      queryClient?.clear();
    } finally {
      // Always leave the authenticated shell, even if sign-out rejects
      // (network error, stale session): otherwise the route stays mounted and
      // its queries refetch against a dead session.
      window.location.href = redirectTo;
    }
  },

  register: async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }): Promise<{ token: string; accountId: string } | null> => {
    const result = await authClient.signUp.email({
      email,
      password,
      name: email.split('@')[0] || email, // Use email prefix as default name
    });

    if (result.error) {
      const errorMessage = result.error.message || '';
      if (errorMessage.includes('already exists') || result.error.status === 409) {
        toast.error('Account Already Exists', { description: 'Try signing in instead.' });
      } else {
        toast.error('Could Not Register', { description: errorMessage || 'Please try again.' });
      }
      return null;
    }

    const userId = result.data?.user?.id;
    if (!userId) {
      return null;
    }

    return {
      token: '', // Sessions use cookies now
      accountId: userId,
    };
  },
};
